import { NgModule, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { APP_BASE_HREF, CommonModule, DatePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, provideRouter, withRouterConfig } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { A11yModule } from '@angular/cdk/a11y';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app.component';
import { routes } from './app-routes';
import { CommonDirectivesModule } from '@common/directives/commonDirectives.module';
import { MaterialModule } from '@common/module/material.modules';
import { NgxDaterangepickerMd } from '@common/components/daterangepicker';
import { TranslocoRootModule } from '@common/module/transloco.module';
import { LibraryIQToastComponent } from '@common/components/toast.component';
import { LiqChartsModule } from '@common/components/charts/liq-charts.module';
import { LiqDoc360Component } from '@common/components/doc360.component';
import { tokenInterceptor } from '@common/interceptors/token.interceptor';
import { publicDashboardInterceptor } from '@common/interceptors/public-dashboard.interceptor';
import { PosthogService } from '@services/posthog.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { AppLocaleService } from '@services/app-locale.service';
import { provideLiqDateAdapter } from '@common/localization/liq-date-adapter';
import { bootLocale } from './locale-bootstrap';

// AG-Grid setup
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import {
    RowNumbersModule,
    FindModule,
    SetFilterModule,
    MultiFilterModule,
    AdvancedFilterModule,
    CellSelectionModule,
    RichSelectModule,
    BatchEditModule,
    RowApiModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    GroupFilterModule,
    SideBarModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    NewFiltersToolPanelModule,
    ColumnMenuModule,
    ContextMenuModule,
    StatusBarModule,
    ExcelExportModule,
    ClipboardModule,
    MasterDetailModule
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
    AllCommunityModule,
    RowNumbersModule,
    FindModule,
    SetFilterModule,
    MultiFilterModule,
    AdvancedFilterModule,
    CellSelectionModule,
    RichSelectModule,
    BatchEditModule,
    RowApiModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    GroupFilterModule,
    SideBarModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    NewFiltersToolPanelModule,
    ColumnMenuModule,
    ContextMenuModule,
    StatusBarModule,
    ExcelExportModule,
    ClipboardModule,
    MasterDetailModule
]);

// PostHog initialization factory
export function initializePostHog(posthogService: PosthogService): () => Promise<void> {
    return async () => {
        try {
            await posthogService.initialize();
        } catch (error) {
            console.error('Failed to initialize PostHog during app startup:', error);
        }
    };
}

/**
 * APP_INITIALIZER factory that starts watching the user and library so the
 * date locale follows them. Doesn't block startup.
 */
export function initializeLocale(appLocaleService: AppLocaleService): () => void {
    return () => appLocaleService.init();
}

/**
 * APP_INITIALIZER factory that eagerly loads feature flags before any
 * route activates. This ensures the MyHomepage flag (and any future
 * flags) are resolved before the landing page decides what to render,
 * eliminating the flash of wrong content.
 */
export function initializeFeatureFlags(featureFlagService: FeatureFlagService): () => Promise<void> {
    return async () => {
        try {
            await featureFlagService.initialize();
        } catch (error) {
            // Swallow the error so app initialization is never blocked by feature flag failures
            console.error('Failed to initialize feature flags during app startup:', error);
        }
    };
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    exports: [MaterialModule],
    imports: [
        ToastrModule.forRoot({
            timeOut: 2000,
            preventDuplicates: true,
            closeButton: true,
            toastComponent: LibraryIQToastComponent
        }),
        RouterModule,
        BrowserModule,
        A11yModule,
        CommonModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        CommonDirectivesModule,
        ScrollingModule,
        NgxDaterangepickerMd.forRoot(),
        TranslocoRootModule,
        LiqChartsModule,
        LiqDoc360Component
    ],
    providers: [
        DatePipe,
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: LOCALE_ID, useValue: bootLocale.angular },
        // One dayjs date adapter for every Material datepicker, in the locale above
        // (MAT_DATE_LOCALE defaults to LOCALE_ID).
        ...provideLiqDateAdapter(),
        provideHttpClient(withInterceptors([publicDashboardInterceptor, tokenInterceptor])),
        provideRouter(
            routes,
            withRouterConfig({
                canceledNavigationResolution: 'computed'
            })
        ),
        {
            provide: APP_INITIALIZER,
            useFactory: initializeFeatureFlags,
            deps: [FeatureFlagService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeLocale,
            deps: [AppLocaleService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializePostHog,
            deps: [PosthogService],
            multi: true
        }
    ]
})
export class AppModule {}
